import React from "react";
import AppShell from "@/components/layout/AppShell";
import DeleteAccountDialog from "@/components/settings/DeleteAccountDialog";
import ChangeUsernameCard from "@/components/settings/ChangeUsernameCard";
import ChangePasswordCard from "@/components/settings/ChangePasswordCard";
import AppearanceCard from "@/components/settings/AppearanceCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AtSign, Mail, ShieldCheck, Crown, LogOut, Trash2, CalendarDays, Lock, Bell, Shield, UserRound, Globe2, Paintbrush, Info, ChevronRight, UserPlus, MessageCircle, Phone, Heart, ArrowLeft, Languages, Monitor, ShieldAlert, Clock } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";

function Row({ icon: Icon, label, value, testId }) {
  return (
    <div className="flex items-center gap-3 border-b border-slate-100 py-3 last:border-0 dark:border-white/5" data-testid={testId}>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-50 dark:bg-white/[0.05]"><Icon className="h-4 w-4 text-slate-400 dark:text-slate-500" /></span>
      <div className="min-w-0"><p className="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">{label}</p><p className="truncate text-sm font-medium text-slate-800 dark:text-slate-200">{value || "—"}</p></div>
    </div>
  );
}

const SETTINGS = [
  { key: "notifications", icon: Bell, title: "Notifications", description: "Manage notifications and alerts." },
  { key: "privacy", icon: Shield, title: "Privacy", description: "Control who can see your content and contact you." },
  { key: "roles", icon: Crown, title: "Roles & Abilities", description: "Enable, disable and customize your active role." },
  { key: "perks", icon: Paintbrush, title: "Perks & Styling", description: "Manage active perks, username color and chat cosmetics." },
  { key: "account", icon: UserRound, title: "Account", description: "Manage your account details and security." },
  { key: "language", icon: Globe2, title: "Language", description: "Choose your preferred language." },
  { key: "theme", icon: Paintbrush, title: "Theme & Display", description: "Customize your Yaprio experience." },
  { key: "about", icon: Info, title: "About", description: "Version, help and more." },
];

function SettingLink({ icon: Icon, title, description, active, onClick, testId }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick} aria-pressed={active} className={`group flex w-full items-center gap-4 rounded-2xl border p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-sm ${active ? "border-orange-300 bg-orange-50/80 dark:border-orange-500/30 dark:bg-orange-500/[0.08]" : "border-slate-100 bg-white hover:border-orange-200 dark:border-white/[0.07] dark:bg-[#171b24] dark:hover:border-orange-500/30 dark:hover:bg-[#1b202b]"}`}>
      <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${active ? "bg-orange-100 dark:bg-orange-500/15" : "bg-orange-50 dark:bg-orange-500/[0.08]"}`}><Icon className={`h-5 w-5 ${active ? "text-orange-600 dark:text-orange-300" : "text-orange-500 dark:text-orange-300"}`} /></span>
      <span className="min-w-0 flex-1"><span className="block font-heading text-base font-bold text-slate-900 dark:text-slate-100">{title}</span><span className="mt-0.5 block text-sm text-slate-500 dark:text-slate-400">{description}</span></span>
      <ChevronRight className={`h-5 w-5 shrink-0 transition-transform group-hover:translate-x-0.5 ${active ? "text-orange-500" : "text-slate-300 dark:text-slate-600"}`} />
    </button>
  );
}

const DEFAULT_NOTIFICATIONS = { enabled: true, friendRequests: true, messages: true, calls: true, socialActivity: true };

function NotificationSettingsCard() {
  const { t } = useLanguage();
  const [prefs, setPrefs] = React.useState(() => {
    try { return { ...DEFAULT_NOTIFICATIONS, ...JSON.parse(localStorage.getItem("yaprio-notification-settings") || "{}") }; }
    catch { return DEFAULT_NOTIFICATIONS; }
  });
  const update = (key, value) => {
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    localStorage.setItem("yaprio-notification-settings", JSON.stringify(next));
  };
  const options = [
    { key: "friendRequests", icon: UserPlus, label: "Friend requests", description: "Requests and friend request updates." },
    { key: "messages", icon: MessageCircle, label: "Messages", description: "New message notifications." },
    { key: "calls", icon: Phone, label: "Calls", description: "Incoming and missed call alerts." },
    { key: "socialActivity", icon: Heart, label: "Social activity", description: "Likes, comments and other activity." },
  ];
  return (
    <Card className="rounded-3xl border-slate-100 bg-white p-6 shadow-sm dark:border-white/[0.07] dark:bg-[#141820]" data-testid="settings-notifications-card">
      <div className="mb-1 flex items-center gap-2"><Bell className="h-4 w-4 text-pink-500" /><h2 className="font-heading text-lg font-bold text-slate-900 dark:text-slate-100">{t("Notifications")}</h2></div>
      <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">Choose which alerts Yaprio can show you on this device.</p>
      <div className="divide-y divide-slate-100 dark:divide-white/5">
        <div className="flex items-center justify-between gap-4 py-4"><div><p className="font-semibold text-slate-900 dark:text-slate-100">Notifications</p><p className="text-sm text-slate-500 dark:text-slate-400">{t("Turn all notification alerts on or off.")}</p></div><Switch checked={prefs.enabled} onCheckedChange={(v) => update("enabled", v)} aria-label="Enable notifications" data-testid="notifications-master-switch" /></div>
        {options.map(({ key, icon: Icon, label, description }) => (
          <div key={key} className={`flex items-center justify-between gap-4 py-4 ${!prefs.enabled ? "opacity-50" : ""}`}><div className="flex min-w-0 items-center gap-3"><span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-50 dark:bg-white/[0.05]"><Icon className="h-4 w-4 text-slate-400 dark:text-slate-500" /></span><div><p className="font-semibold text-slate-900 dark:text-slate-100">{label}</p><p className="text-sm text-slate-500 dark:text-slate-400">{description}</p></div></div><Switch checked={prefs[key]} disabled={!prefs.enabled} onCheckedChange={(v) => update(key, v)} aria-label={label} data-testid={`notifications-${key}-switch`} /></div>
        ))}
      </div>
      <p className="mt-3 text-xs text-slate-400 dark:text-slate-500">Your choices are saved automatically on this device.</p>
    </Card>
  );
}

function PrivacySettingsCard() {
  const { t } = useLanguage();
  const DEFAULTS = {
    profile_visibility: "everyone",
    photo_visibility: "everyone",
    allow_messages: "friends",
    allow_friend_requests: "everyone",
    allow_calls: "friends",
    discoverable_random: true,
  };
  const [prefs, setPrefs] = React.useState(DEFAULTS);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;
    api.get("/settings/privacy")
      .then(({ data }) => mounted && setPrefs({ ...DEFAULTS, ...data }))
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  // DEFAULTS is a stable module-level preference template for this one-time fetch.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = async (key, value) => {
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    setSaving(true);
    try {
      const { data } = await api.put("/settings/privacy", next);
      setPrefs({ ...DEFAULTS, ...data });
    } catch (e) {
      setPrefs(prefs);
      toast.error(e.response?.data?.detail || "Couldn't save privacy settings.");
    } finally {
      setSaving(false);
    }
  };

  const Choice = ({ label, value, options, onChange }) => (
    <div className="grid grid-cols-2 gap-2">
      {options.map((option) => (
        <button key={option.value} type="button" onClick={() => onChange(option.value)}
          className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition ${value === option.value
            ? "border-pink-300 bg-pink-50 text-pink-600 dark:border-pink-500/30 dark:bg-pink-500/[0.08] dark:text-pink-300"
            : "border-slate-100 bg-slate-50 text-slate-600 hover:border-slate-200 dark:border-white/[0.07] dark:bg-white/[0.025] dark:text-slate-300"}`}
          aria-pressed={value === option.value}>{option.label}</button>
      ))}
    </div>
  );

  const ToggleRow = ({ icon: Icon, title, description, value, onChange }) => (
    <div className="flex items-center justify-between gap-4 py-4">
      <div className="flex min-w-0 items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-50 dark:bg-white/[0.05]">
          <Icon className="h-4 w-4 text-slate-400" />
        </span>
        <div><p className="font-semibold text-slate-900 dark:text-slate-100">{title}</p><p className="text-sm text-slate-500 dark:text-slate-400">{description}</p></div>
      </div>
      <Switch checked={value} onCheckedChange={onChange} disabled={saving || loading} aria-label={title} />
    </div>
  );

  return (
    <Card className="rounded-3xl border-slate-100 bg-white p-6 shadow-sm dark:border-white/[0.07] dark:bg-[#141820]" data-testid="settings-privacy-card">
      <div className="mb-1 flex items-center gap-2"><Shield className="h-4 w-4 text-pink-500" /><h2 className="font-heading text-lg font-bold text-slate-900 dark:text-slate-100">{t("Privacy")}</h2></div>
      <p className="mb-5 text-sm text-slate-500 dark:text-slate-400">Control who can see you and who can contact you. Changes save automatically.</p>

      <div className="divide-y divide-slate-100 dark:divide-white/5">
        <div className="py-4">
          <p className="mb-1 font-semibold text-slate-900 dark:text-slate-100">Profile visibility</p>
          <p className="mb-3 text-sm text-slate-500 dark:text-slate-400">Choose who can open your profile.</p>
          <Choice value={prefs.profile_visibility} onChange={(v) => update("profile_visibility", v)} options={[
            { value: "everyone", label: "Everyone" }, { value: "nobody", label: "Private" }
          ]} />
        </div>
        <div className="py-4">
          <p className="mb-1 font-semibold text-slate-900 dark:text-slate-100">Photo visibility</p>
          <p className="mb-3 text-sm text-slate-500 dark:text-slate-400">Control who can view your profile gallery.</p>
          <Choice value={prefs.photo_visibility} onChange={(v) => update("photo_visibility", v)} options={[
            { value: "everyone", label: "Everyone" }, { value: "nobody", label: "Private" }
          ]} />
        </div>
        <div className="py-4">
          <p className="mb-1 font-semibold text-slate-900 dark:text-slate-100">Friend requests</p>
          <p className="mb-3 text-sm text-slate-500 dark:text-slate-400">Choose whether anyone can send you a request.</p>
          <Choice value={prefs.allow_friend_requests} onChange={(v) => update("allow_friend_requests", v)} options={[
            { value: "everyone", label: "Everyone" }, { value: "nobody", label: "Nobody" }
          ]} />
        </div>
        <div className="py-4">
          <p className="mb-1 font-semibold text-slate-900 dark:text-slate-100">Private messages</p>
          <p className="mb-3 text-sm text-slate-500 dark:text-slate-400">Control whether friends can message you.</p>
          <Choice value={prefs.allow_messages} onChange={(v) => update("allow_messages", v)} options={[
            { value: "friends", label: "Friends" }, { value: "nobody", label: "Nobody" }
          ]} />
        </div>
        <div className="py-4">
          <p className="mb-1 font-semibold text-slate-900 dark:text-slate-100">Calls</p>
          <p className="mb-3 text-sm text-slate-500 dark:text-slate-400">Control who can call you.</p>
          <Choice value={prefs.allow_calls} onChange={(v) => update("allow_calls", v)} options={[
            { value: "friends", label: "Friends" }, { value: "nobody", label: "Nobody" }, { value: "everyone", label: "—" }
          ]} />
        </div>
        <div className="flex items-center justify-between gap-4 py-4">
          <div>
            <p className="font-semibold text-slate-900 dark:text-slate-100">Random video chat</p>
            <p className="text-sm text-slate-500 dark:text-slate-400">Allow Yaprio to match you with other members in Random Video Chat.</p>
          </div>
          <Switch checked={prefs.discoverable_random} onCheckedChange={(v) => update("discoverable_random", v)} disabled={saving || loading} aria-label="Allow random video chat" data-testid="privacy-random-video-switch" />
        </div>
      </div>
      {loading && <p className="mt-4 text-xs text-slate-400">Loading privacy settings…</p>}
      {!loading && <p className="mt-4 text-xs text-slate-400">{saving ? "Saving…" : "All changes are saved."}</p>}
    </Card>
  );
}
function RoleSettingsCard() {
  const { t } = useLanguage();
  const [data, setData] = React.useState({ role: null, enabled: false, expires_at: null, abilities: {}, labels: {} });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [appearance, setAppearance] = React.useState({ ring_color: "#f97316", ring_animated: false, ring_style: "aurora", owned_rings: [], profile_banner_url: null });
  const [bannerUploading, setBannerUploading] = React.useState(false);
  React.useEffect(() => { api.get("/profile/me").then(({ data: me }) => setAppearance({ ring_color: me.profile_ring_color || "#f97316", ring_animated: !!me.profile_ring_animated, ring_style: me.profile_ring_style || "aurora", owned_rings: me.owned_rings || [], profile_banner_url: me.profile_banner_url || me.profile_banner_path || null })).catch(() => {}); }, []);

  React.useEffect(() => {
    let mounted = true;
    api.get("/economy/role-settings")
      .then(({ data }) => mounted && setData(data))
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const save = async (next) => {
    setData(next);
    setSaving(true);
    try {
      const { data: saved } = await api.put("/economy/role-settings", {
        enabled: next.enabled,
        abilities: next.abilities,
      });
      setData(saved);
      try { await refresh?.(); } catch (_) {}
      toast.success("Role settings saved.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save role settings.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Card className="rounded-3xl border-slate-100 bg-white p-6 shadow-sm dark:border-white/[0.07] dark:bg-[#141820]"><p className="text-sm text-slate-400">Loading role settings…</p></Card>;

  if (!data.role) return (
    <Card className="rounded-3xl border-slate-100 bg-white p-6 shadow-sm dark:border-white/[0.07] dark:bg-[#141820]" data-testid="settings-roles-card">
      <div className="flex items-center gap-2"><Crown className="h-4 w-4 text-pink-500" /><h2 className="font-heading text-lg font-bold text-slate-900 dark:text-slate-100">Roles & Abilities</h2></div>
      <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">You don't currently own a role. Buy one from Wallet & Store to unlock role abilities.</p>
    </Card>
  );

  const saveAppearance = async (next) => {
    setAppearance(next);
    try { await api.put("/profile/appearance", next); toast.success("Profile appearance saved."); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't save profile appearance."); }
  };
  const uploadBanner = async (event) => {
    const file = event.target.files?.[0]; if (!file) return;
    const form = new FormData(); form.append("file", file); setBannerUploading(true);
    try {
      const { data: me } = await api.post("/profile/banner", form, { headers: { "Content-Type": "multipart/form-data" } });
      setAppearance((old) => ({ ...old, profile_banner_url: me.profile_banner_url || me.profile_banner_path || "uploaded" }));
      toast.success("Profile banner uploaded.");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed."); }
    finally { setBannerUploading(false); event.target.value = ""; }
  };
  const removeBanner = async () => {
    setBannerUploading(true);
    try { await api.delete("/profile/banner"); setAppearance((old) => ({ ...old, profile_banner_url: null })); toast.success("Profile banner removed."); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove banner."); }
    finally { setBannerUploading(false); }
  };
  const expiry = data.expires_at ? new Date(data.expires_at) : null;
  const daysLeft = expiry ? Math.max(0, Math.ceil((expiry.getTime() - Date.now()) / 86400000)) : null;

  return (
    <Card className="rounded-3xl border-slate-100 bg-white p-6 shadow-sm dark:border-white/[0.07] dark:bg-[#141820]" data-testid="settings-roles-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2"><Crown className="h-4 w-4 text-pink-500" /><h2 className="font-heading text-lg font-bold text-slate-900 dark:text-slate-100">{data.emoji} {data.name}</h2></div>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Your role lasts 30 days. Turn it off or disable individual abilities whenever you want.</p>
        </div>
        <Switch checked={data.enabled} disabled={saving} onCheckedChange={(enabled) => save({ ...data, enabled })} aria-label="Enable role" data-testid="role-enabled-switch" />
      </div>


      <div className="mt-5 rounded-2xl bg-slate-50 p-4 dark:bg-white/[0.035]">
        <div className="flex items-center gap-2 text-sm font-bold text-slate-800 dark:text-slate-200"><Clock className="h-4 w-4 text-pink-500" />{daysLeft} day{daysLeft === 1 ? "" : "s"} remaining</div>
        <p className="mt-1 text-xs text-slate-400">Expires {expiry?.toLocaleString()}</p>
      </div>

      <div className="mt-4 divide-y divide-slate-100 dark:divide-white/5">
        {Object.entries(data.abilities).map(([key, value]) => (
          <div key={key} className={`flex items-center justify-between gap-4 py-4 ${!data.enabled ? "opacity-50" : ""}`}>
            <div>
              <p className="font-semibold text-slate-900 dark:text-slate-100">{data.labels?.[key] || key}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">{key === "yc_earnings" ? "Use your role's higher YC earnings when messaging." : key === "daily_login" ? "Use your role's boosted daily login reward." : "Show your role badge on your profile and chats."}</p>
            </div>
            <Switch checked={value} disabled={saving || !data.enabled} onCheckedChange={(enabled) => save({ ...data, abilities: { ...data.abilities, [key]: enabled } })} aria-label={data.labels?.[key] || key} data-testid={`role-ability-${key}-switch`} />
          </div>
        ))}
      </div>
      <div className="mt-5 border-t border-slate-100 pt-5 dark:border-white/5">
        <h3 className="font-heading text-base font-bold text-slate-900 dark:text-slate-100">Role profile perks</h3>
        
        <div className="mt-4 flex flex-wrap items-center gap-3"><label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold hover:border-orange-300 dark:border-white/10">{bannerUploading ? "Please wait…" : appearance.profile_banner_url ? "Replace profile banner" : "Upload profile banner"}<input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" disabled={bannerUploading} onChange={uploadBanner} /></label>{appearance.profile_banner_url && <><span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600"><span className="h-2 w-2 rounded-full bg-emerald-500" />Banner uploaded</span><Button type="button" variant="outline" disabled={bannerUploading} onClick={removeBanner} className="rounded-xl text-red-600"><Trash2 className="mr-2 h-4 w-4" />Remove banner</Button></>}</div>
        <div className="mt-4"><p className="mb-2 text-sm font-semibold text-slate-800 dark:text-slate-200">{t("Avatar ring color")}</p><div className="flex flex-wrap gap-2">{["#f97316", "#ec4899", "#8b5cf6", "#3b82f6", "#10b981", "#ef4444", "#eab308"].map((color) => <button key={color} type="button" aria-label={`Choose ring color ${color}`} onClick={() => saveAppearance({ ...appearance, ring_color: color })} className={`h-9 w-9 rounded-full border-2 ${appearance.ring_color === color ? "border-slate-900 ring-2 ring-orange-300 dark:border-white" : "border-white dark:border-slate-800"}`} style={{ backgroundColor: color }} />)}</div></div>

      </div>
    </Card>
  );
}

function PerkSettingsCard({ refresh }) {
  const { t } = useLanguage();
  const [data, setData] = React.useState({ perks: {}, username_color: null, username_colors: ["pink", "purple", "blue", "green", "orange"] });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [ringAppearance, setRingAppearance] = React.useState({ ring_color: "#f97316", ring_animated: false, ring_style: "aurora", owned_rings: [], role: null });
  React.useEffect(() => { api.get("/profile/me").then(({ data: me }) => setRingAppearance({ ring_color: me.profile_ring_color || "#f97316", ring_animated: !!me.profile_ring_animated, ring_style: me.profile_ring_style || "aurora", owned_rings: me.owned_rings || [], role: me.yaprio_role || me.profile_role || null })).catch(() => {}); }, []);
  const saveRingAppearance = async (next) => { setRingAppearance(next); try { await api.put("/profile/appearance", next); toast.success("Avatar ring saved."); } catch (e) { toast.error(e.response?.data?.detail || "Could not save avatar ring."); } };

  const perkMeta = {
    ghost_mode: { name: "Ghost Mode", emoji: "👻", description: "Hide read receipts while reading messages." },
    profile_frame: { name: "Profile Frame", emoji: "🖼️", description: "Show your premium frame around your profile photo." },
    username_color: { name: "Username Color", emoji: "🎨", description: "Choose a custom color for your username across Yaprio." },
    chat_badge: { name: "Chat Badge", emoji: "💬", description: "Show the chat badge beside your username." },
  };
  const colorValues = { pink: "#ec4899", purple: "#8b5cf6", blue: "#3b82f6", green: "#10b981", orange: "#f97316" };

  React.useEffect(() => {
    let mounted = true;
    api.get("/economy/perk-settings")
      .then(({ data }) => mounted && setData(data))
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const save = async (payload) => {
    setSaving(true);
    try {
      const { data: saved } = await api.put("/economy/perk-settings", payload);
      setData(saved);
      try { await refresh?.(); } catch (_) {}
      toast.success("Perk settings saved.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save perk settings.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Card className="rounded-3xl border-slate-100 bg-white p-6 shadow-sm dark:border-white/[0.07] dark:bg-[#141820]"><p className="text-sm text-slate-400">Loading perk settings…</p></Card>;

  const ownedKeys = Object.keys(data.perks || {});
  return (
    <Card className="rounded-3xl border-slate-100 bg-white p-6 shadow-sm dark:border-white/[0.07] dark:bg-[#141820]" data-testid="settings-perks-card">
      <div className="mb-1 flex items-center gap-2"><Paintbrush className="h-4 w-4 text-pink-500" /><h2 className="font-heading text-lg font-bold text-slate-900 dark:text-slate-100">{t("Perks & Styling")}</h2></div>
      <p className="mb-5 text-sm text-slate-500 dark:text-slate-400">Turn your active perks on or off. Every perk lasts 30 days from purchase.</p>

      {ownedKeys.length === 0 ? (
        <div className="rounded-2xl bg-slate-50 p-5 text-center dark:bg-white/[0.035]">
          <p className="font-semibold text-slate-800 dark:text-slate-200">No active perks</p>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{t("Buy perks from Wallet & Store to customize your profile and chats.")}</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100 dark:divide-white/5">
          {ownedKeys.map((key) => {
            const meta = perkMeta[key] || { name: key, emoji: "✨", description: "Premium Yaprio perk." };
            const item = data.perks[key];
            return (
              <div key={key} className="py-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-pink-50 text-lg dark:bg-pink-500/[0.08]">{meta.emoji}</span>
                    <div className="min-w-0"><p className="font-semibold text-slate-900 dark:text-slate-100">{meta.name}</p><p className="text-sm text-slate-500 dark:text-slate-400">{meta.description}</p></div>
                  </div>
                  <Switch checked={item.enabled} disabled={saving} onCheckedChange={(enabled) => save({ perk: key, enabled })} aria-label={`Enable ${meta.name}`} data-testid={`perk-${key}-switch`} />
                </div>
                {key === "username_color" && item.enabled && (
                  <div className="mt-4 rounded-2xl bg-slate-50 p-4 dark:bg-white/[0.035]">
                    <p className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-400">Username color</p>
                    <div className="flex flex-wrap gap-3">
                      {(data.username_colors || []).map((color) => (
                        <button key={color} type="button" onClick={() => save({ perk: "username_color", color })} disabled={saving}
                          className={`flex items-center gap-2 rounded-full border px-3 py-2 text-xs font-bold capitalize transition ${data.username_color === color ? "border-pink-300 bg-white shadow-sm dark:border-pink-500/30 dark:bg-slate-900" : "border-transparent bg-white/70 dark:bg-slate-900/50"}`} aria-pressed={data.username_color === color}>
                          <span className="h-3 w-3 rounded-full" style={{ backgroundColor: colorValues[color] || "#ec4899" }} />{color}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {item.expires_at && <p className="mt-2 text-[11px] text-slate-400">Expires {new Date(item.expires_at).toLocaleDateString("ja-JP")}</p>}
              </div>
            );
          })}
        </div>
      )}
      {(["elite", "legend"].includes(String(ringAppearance.role || "").toLowerCase()) || (ringAppearance.owned_rings || []).length > 0) && (
        <div className="mt-6 border-t border-slate-100 pt-5 dark:border-white/5" data-testid="purchased-animated-rings">
          <div className="mb-1 flex items-center gap-2"><Paintbrush className="h-4 w-4 text-orange-500" /><h3 className="font-heading text-base font-bold text-slate-900 dark:text-slate-100">Animated Rings</h3></div>
          <p className="mb-3 text-sm text-slate-500 dark:text-slate-400">Manage the animated rings you own. Elite and Legend can use all designs; individually purchased rings are available here too.</p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {[{id:"aurora",name:"Aurora",desc:"Flowing neon gradient"},{id:"blaze",name:"Blaze",desc:"Flame-inspired glow"},{id:"volt",name:"Volt",desc:"Electric pulse"},{id:"orbit",name:"Orbit",desc:"Orbiting light"},{id:"nova",name:"Nova",desc:"Gold segmented glow"}].filter((ring) => ["elite","legend"].includes(String(ringAppearance.role || "").toLowerCase()) || (ringAppearance.owned_rings || []).includes(ring.id)).map((ring) => <button key={ring.id} type="button" onClick={() => saveRingAppearance({ ...ringAppearance, ring_style: ring.id, ring_animated: true })} aria-pressed={ringAppearance.ring_style === ring.id} className={"flex items-center gap-3 rounded-xl border p-3 text-left transition " + (ringAppearance.ring_style === ring.id ? "border-orange-400 bg-orange-50/70 dark:bg-orange-500/10" : "border-slate-200 dark:border-white/10")}>
              <span className={"yaprio-avatar-ring ring-" + ring.id + " h-10 w-10 shrink-0 rounded-full"} style={{ "--ring-color": ringAppearance.ring_color }}><span /></span>
              <span><span className="block text-sm font-bold text-slate-900 dark:text-slate-100">{ring.name}</span><span className="block text-xs text-slate-500 dark:text-slate-400">{ring.desc}</span></span>
              {ringAppearance.ring_style === ring.id && <span className="ml-auto text-xs font-bold text-orange-500">{t("Selected")}</span>}
            </button>)}
          </div>
          <div className="mt-3 flex items-center justify-between gap-4"><div><p className="font-semibold text-slate-900 dark:text-slate-100">{t("Animated avatar ring")}</p><p className="text-sm text-slate-500 dark:text-slate-400">{t("Turn your selected effect on or off.")}</p></div><Switch checked={ringAppearance.ring_animated} onCheckedChange={(ring_animated) => saveRingAppearance({ ...ringAppearance, ring_animated })} aria-label="Animated avatar ring" /></div>
        </div>
      )}
    </Card>
  );
}

function LanguageCard() {
  const { language, setLanguage, t } = useLanguage();
  const choose = (value) => setLanguage(value);
  return (
    <Card className="rounded-3xl border-slate-100 bg-white p-6 shadow-sm dark:border-white/[0.07] dark:bg-[#141820]" data-testid="settings-language-card">
      <div className="mb-1 flex items-center gap-2"><Languages className="h-4 w-4 text-pink-500" /><h2 className="font-heading text-lg font-bold text-slate-900 dark:text-slate-100">Language</h2></div>
      <p className="mb-5 text-sm text-slate-500 dark:text-slate-400">{t("Choose your preferred language.")}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {[{ value: "en", label: "English" }, { value: "ja", label: "Japanese · 日本語" }].map((item) => <button key={item.value} type="button" onClick={() => choose(item.value)} className={`rounded-2xl border p-4 text-left font-semibold transition ${language === item.value ? "border-pink-300 bg-pink-50 text-pink-600 dark:border-pink-500/30 dark:bg-pink-500/[0.08] dark:text-pink-300" : "border-slate-100 bg-slate-50 text-slate-700 hover:border-slate-200 dark:border-white/[0.07] dark:bg-white/[0.025] dark:text-slate-300"}`} aria-pressed={language === item.value} data-testid={`language-${item.value === "en" ? "english" : "japanese"}`}>{item.label}</button>)}
      </div>
    </Card>
  );
}

function AboutCard() {
  return (
    <Card className="rounded-3xl border-slate-100 bg-white p-6 shadow-sm dark:border-white/[0.07] dark:bg-[#141820]" data-testid="settings-about-card">
      <div className="mb-1 flex items-center gap-2"><Info className="h-4 w-4 text-pink-500" /><h2 className="font-heading text-lg font-bold text-slate-900 dark:text-slate-100">About Yaprio</h2></div>
      <p className="mb-5 text-sm text-slate-500 dark:text-slate-400">Everything you need to know about this version of Yaprio.</p>
      <div className="divide-y divide-slate-100 rounded-2xl border border-slate-100 dark:divide-white/5 dark:border-white/[0.06]">
        <Row icon={Info} label="Version" value="0.1.0" testId="settings-version" />
        <Row icon={ShieldCheck} label="Safety" value="Your safety and privacy come first" testId="settings-safety" />
        <Row icon={Monitor} label="Platform" value="Yaprio web" testId="settings-platform" />
      </div>
    </Card>
  );
}

export default function SettingsPage() {
  const { t } = useLanguage();
  const { user, logout, refresh } = useAuth();
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [section, setSection] = React.useState(() => window.location.hash.replace("#settings-", "") || "");

  React.useEffect(() => {
    const onPopState = () => setSection(window.location.hash.replace("#settings-", "") || "");
    window.addEventListener("popstate", onPopState);
    window.addEventListener("hashchange", onPopState);
    return () => { window.removeEventListener("popstate", onPopState); window.removeEventListener("hashchange", onPopState); };
  }, []);

  const openSection = (key) => {
    setSection(key);
    window.history.pushState({ settings: key }, "", `#settings-${key}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const active = SETTINGS.find((item) => item.key === section) || null;

  return (
    <AppShell>
      <div data-testid="settings-page">
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100 sm:text-3xl">{t("Settings")}</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{t("Manage your Yaprio experience, account and preferences.")}</p>
      </div>

      {!active && (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="settings-options">
          {SETTINGS.map((item) => <SettingLink key={item.key} {...item} title={t(item.title)} description={t(item.description)} active={false} onClick={() => openSection(item.key)} testId={`settings-${item.key === "theme" ? "theme-display" : item.key}`} />)}
        </div>
      )}

      {active && (
        <>
          <div className="flex items-center gap-3 pt-2"><button type="button" onClick={() => { setSection(""); window.history.pushState({}, "", window.location.pathname); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="flex items-center gap-2 text-sm font-semibold text-slate-500 transition hover:text-pink-500 dark:text-slate-400"><ArrowLeft className="h-4 w-4" />{t("Settings categories")}</button><span className="text-slate-300 dark:text-slate-700">/</span><span className="text-sm font-semibold text-slate-800 dark:text-slate-200">{t(active.title)}</span></div>

          {active.key === "notifications" && <NotificationSettingsCard />}
          {active.key === "privacy" && <PrivacySettingsCard />}
          {active.key === "roles" && <RoleSettingsCard />}
          {active.key === "perks" && <PerkSettingsCard refresh={refresh} />}
          {active.key === "language" && <LanguageCard />}
          {active.key === "theme" && <AppearanceCard />}
          {active.key === "about" && <AboutCard />}

          {active.key === "account" && (
            <div className="space-y-4">
              <Card className="rounded-3xl border-slate-100 bg-white p-6 shadow-sm dark:border-white/[0.07] dark:bg-[#141820]" data-testid="settings-account-card">
                <div className="flex items-center gap-4"><Avatar className="h-14 w-14 ring-4 ring-pink-50 dark:ring-pink-500/10"><AvatarImage src={mediaUrl(user?.avatar_url)} alt={user?.username} /><AvatarFallback className="bg-pink-500 font-bold text-white">{(user?.username || "?").charAt(0).toUpperCase()}</AvatarFallback></Avatar><div className="min-w-0"><p className="font-heading text-lg font-extrabold text-slate-900 dark:text-slate-100">@{user?.username}</p><p className="text-sm text-slate-400 dark:text-slate-500">{user?.role === "admin" ? "Admin account" : user?.role === "moderator" ? "Moderator account" : "Yaprio member"}</p></div></div>
                <div className="mt-4 border-t border-slate-50 pt-1 dark:border-white/5"><Row icon={AtSign} label="Username" value={`@${user?.username}`} testId="settings-username" /><Row icon={Mail} label="Email" value={user?.email} testId="settings-email" /><Row icon={user?.auth_provider === "google" ? ShieldCheck : Lock} label="Sign-in method" value={user?.auth_provider === "google" ? "Google account" : "Email & password"} testId="settings-provider" /><Row icon={CalendarDays} label="Member since" value={user?.created_at ? new Date(user.created_at).toLocaleDateString("ja-JP") : "—"} testId="settings-created" /></div>
                <p className="mt-4 rounded-xl bg-slate-50 p-3 text-xs text-slate-500 dark:bg-white/[0.04] dark:text-slate-400">Your @username is your public identity. Your email address is never shown to other members.</p>
              </Card>
              <ChangeUsernameCard />
              <ChangePasswordCard />
              <Card className="rounded-3xl border-slate-100 bg-white p-6 shadow-sm dark:border-white/[0.07] dark:bg-[#141820]" data-testid="settings-session-card"><h2 className="font-heading text-lg font-bold text-slate-900 dark:text-slate-100">Session</h2><p className="mb-4 mt-1 text-sm text-slate-500 dark:text-slate-400">Sign out of Yaprio on this device.</p><Button variant="outline" onClick={logout} data-testid="settings-logout-btn" className="rounded-full border-slate-200 font-semibold dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-200"><LogOut className="mr-2 h-4 w-4" />Log out</Button></Card>
              <Card className="rounded-3xl border-red-100 bg-red-50/40 p-6 shadow-sm dark:border-red-500/20 dark:bg-red-500/[0.06]" data-testid="settings-danger-card"><h2 className="font-heading text-lg font-bold text-red-600 dark:text-red-400">Danger zone</h2><p className="mb-4 mt-1 text-sm text-slate-600 dark:text-slate-400">Permanently delete your account and everything in it. This can&apos;t be undone, and your username is released for someone else to claim.</p><Button onClick={() => setDeleteOpen(true)} data-testid="settings-delete-account-btn" className="rounded-full bg-red-500 text-white hover:bg-red-600"><Trash2 className="mr-2 h-4 w-4" />Delete my account</Button></Card>
              <DeleteAccountDialog open={deleteOpen} onOpenChange={setDeleteOpen} />
            </div>
          )}
        </>
      )}
    </AppShell>
  );
}
